import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Award, Building2, KeyRound, Lock, LogOut, Plane, Plus, Ship, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { CURRENT_CLIENT_ID } from "@/data/constants";
import { Avatar, Card, CardTitle, avatarUrl, btn, normalizePhone, isPhoneKey, normalizePhoneOnBlur } from "@/components/common";
import { CardsPanel } from "@/components/PaymentAuthorizations";
import { PushMasterToggle } from "@/components/NotificationBell";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 disabled:bg-slate-50 disabled:text-slate-400 sm:text-sm";
const lbl = "mb-1.5 block text-xs font-medium text-slate-500";

const TABS = [["contact", "Contact"], ["travel", "Travel"], ["loyalty", "Loyalty"], ["payments", "Payments"], ["account", "Account"]];

const LOYALTY_PROGRAMS = {
  airline: ["Delta SkyMiles", "United MileagePlus", "American AAdvantage", "Southwest Rapid Rewards", "Alaska Mileage Plan", "JetBlue TrueBlue", "British Airways Executive Club", "Air Canada Aeroplan", "Lufthansa Miles & More", "Emirates Skywards"],
  cruise: ["Royal Caribbean Crown & Anchor Society", "Carnival VIFP Club", "Norwegian Latitudes Rewards", "Celebrity Captain's Club", "Princess Captain's Circle", "Holland America Mariner Society", "MSC Voyagers Club", "Disney Castaway Club"],
  hotel: ["Marriott Bonvoy", "Hilton Honors", "World of Hyatt", "IHG One Rewards", "Wyndham Rewards", "Choice Privileges", "Best Western Rewards", "Accor Live Limitless"],
};
const LOYALTY_CATS = [["airline", "Airline"], ["cruise", "Cruise"], ["hotel", "Hotel"]];
const LOYALTY_LABEL = { airline: "Airline", cruise: "Cruise", hotel: "Hotel" };
const LOYALTY_ICON = { airline: Plane, cruise: Ship, hotel: Building2 };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

function LockNotice({ lock, subject }) {
  const who = subject ? `${subject}'s` : "Your";
  return (
    <Card className="border-amber-200 bg-amber-50 p-4" data-testid="client-profile-lock-notice">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700"><Lock className="h-4 w-4" /></span>
        <div>
          <p className="text-sm font-semibold text-amber-900">Editing locked — trip departing soon</p>
          <p className="mt-1 text-sm text-amber-800">{who} contact and travel details are locked because <span className="font-medium">{lock.title}</span> departs {fmtDate(lock.departure)} (within 30 days). Please contact your advisor to make any changes.</p>
        </div>
      </div>
    </Card>
  );
}

function EditForm({ client, testId, fields, readonly = [], footer, locked = false }) {
  const { updateClient } = useDemo();
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const val = (k) => (k in edits ? edits[k] : (client[k] ?? ""));
  const changed = fields.filter(([k]) => k in edits && edits[k] !== (client[k] ?? ""));
  const set = (k) => (e) => setEdits((p) => ({ ...p, [k]: isPhoneKey(k) ? normalizePhone(e.target.value) : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (!changed.length) return;
    setBusy(true);
    const ok = await updateClient(client.id, Object.fromEntries(changed.map(([k]) => [k, edits[k]])));
    setBusy(false);
    if (ok) { setEdits({}); toast.success("Profile updated"); }
  };
  return (
    <Card data-testid={testId}>
      <form onSubmit={save} className="grid gap-4 p-5 sm:grid-cols-2 sm:p-6">
        {readonly.map(([l, v]) => (
          <label key={l}><span className={lbl}>{l}</span><input className={field} value={v || "—"} disabled /></label>
        ))}
        {fields.map(([k, l, type = "text", full]) => (
          <label key={k} className={full || type === "textarea" ? "sm:col-span-2" : ""}>
            <span className={lbl}>{l}</span>
            {type === "textarea"
              ? <textarea rows={2} className={`${field} h-auto py-2.5`} value={val(k)} onChange={set(k)} disabled={locked} data-testid={`client-field-${k}`} />
              : <input type={type} className={field} value={val(k)} onChange={set(k)} onBlur={isPhoneKey(k) ? normalizePhoneOnBlur((n) => setEdits((p) => ({ ...p, [k]: n }))) : undefined} disabled={locked} data-testid={`client-field-${k}`} />}
          </label>
        ))}
        {footer}
        {!locked && <div className="sm:col-span-2"><button disabled={!changed.length || busy} className={btn.primary} data-testid={`${testId}-save`}>Save Changes</button></div>}
      </form>
    </Card>
  );
}

function AddLoyaltyDialog({ client, open, onOpenChange }) {
  const { addLoyalty } = useDemo();
  const [f, setF] = useState({ category: "airline", program: LOYALTY_PROGRAMS.airline[0], number: "" });
  const setCat = (e) => setF({ ...f, category: e.target.value, program: LOYALTY_PROGRAMS[e.target.value][0] });
  const submit = async (e) => {
    e.preventDefault();
    const r = await addLoyalty(client.id, f);
    if (r) { onOpenChange(false); setF({ category: "airline", program: LOYALTY_PROGRAMS.airline[0], number: "" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-5 sm:p-7" data-testid="client-add-loyalty-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Add Loyalty Account</DialogTitle>
          <DialogDescription>Store your airline, cruise or hotel rewards membership.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className={lbl}>Category</span>
            <select className={field} value={f.category} onChange={setCat} data-testid="client-loyalty-category-select">{LOYALTY_CATS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}</select></label>
          <label className="block"><span className={lbl}>Program</span>
            <select className={field} value={f.program} onChange={(e) => setF({ ...f, program: e.target.value })} data-testid="client-loyalty-program-select">{LOYALTY_PROGRAMS[f.category].map((pr) => <option key={pr} value={pr}>{pr}</option>)}</select></label>
          <label className="block"><span className={lbl}>Member number</span>
            <input className={field} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="e.g. 1234567890" data-testid="client-loyalty-number-input" /></label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
            <button className={btn.primary} data-testid="client-loyalty-submit">Add Account</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LoyaltyTab({ client }) {
  const { removeLoyalty } = useDemo();
  const [open, setOpen] = useState(false);
  const list = client.loyalty || [];
  return (
    <Card data-testid="client-loyalty-tab">
      <CardTitle action={<button onClick={() => setOpen(true)} className={btn.ghost} data-testid="client-add-loyalty-btn"><Plus className="h-4 w-4" />Add Account</button>}>Loyalty &amp; rewards</CardTitle>
      <div className="p-5 sm:p-6">
        {list.length ? (
          <ul className="space-y-2">
            {list.map((a) => {
              const Icon = LOYALTY_ICON[a.category] || Award;
              return (
                <li key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid={`client-loyalty-row-${a.id}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy-50 text-navy-700"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-navy-900">{a.program}</p>
                    <p className="truncate text-xs text-slate-500">{LOYALTY_LABEL[a.category]}{a.number ? ` · ${a.number}` : ""}</p>
                  </div>
                  <button onClick={() => removeLoyalty(client.id, a.id)} data-testid={`client-loyalty-remove-${a.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </li>
              );
            })}
          </ul>
        ) : <p className="text-sm text-slate-500">No loyalty accounts yet. Add your airline, cruise and hotel rewards numbers here.</p>}
      </div>
      <AddLoyaltyDialog client={client} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function MemberDialog({ member, onClose }) {
  const [tab, setTab] = useState("contact");
  if (!member) return null;
  const locked = !!member.profileLock;
  const first = member.name.split(" ")[0];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto rounded-2xl p-5 sm:p-6" data-testid="household-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Manage {member.name}</DialogTitle>
          <DialogDescription>Update {first}'s contact, travel and loyalty details. Account sign-in and payments stay private to {first}.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar" data-testid="member-dialog-tabs">
          {[["contact", "Contact"], ["travel", "Travel"], ["loyalty", "Loyalty"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`member-dialog-tab-${k}`} className={cn("rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors", tab === k ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}>{l}</button>
          ))}
        </div>
        <div className="mt-4 space-y-4">
          {locked && tab !== "loyalty" && <LockNotice lock={member.profileLock} subject={first} />}
          {tab === "contact" && (
            <EditForm key={`m-contact-${member.id}`} client={member} testId="member-contact-form" locked={locked}
              readonly={[["Email (sign in)", member.email]]}
              fields={[["phone", "Phone", "tel"], ["dob", "Date of birth", "date"], ["address", "Mailing address", "text", true], ["emergencyName", "Emergency contact name"], ["emergencyPhone", "Emergency contact phone", "tel"]]} />
          )}
          {tab === "travel" && (
            <EditForm key={`m-travel-${member.id}`} client={member} testId="member-travel-form" locked={locked}
              fields={[["passport", "Passport number"], ["passportCountry", "Issuing country"], ["passportIssued", "Issue date", "date"], ["passportExpires", "Expiration date", "date"], ["knownTraveler", "Known traveler number"], ["accessibility", "Accessibility requirements", "textarea"], ["dietary", "Dietary preferences", "textarea"], ["travelPrefs", "Travel preferences", "textarea"]]} />
          )}
          {tab === "loyalty" && <LoyaltyTab client={member} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HouseholdManager({ household }) {
  const [activeId, setActiveId] = useState(null);
  const active = household.find((m) => m.id === activeId) || null;
  return (
    <Card data-testid="client-household-manage">
      <CardTitle>Household Management</CardTitle>
      <div className="p-5 sm:p-6">
        <p className="mb-4 text-sm text-slate-500">As the household primary, you can keep your travelers' contact, travel and loyalty details current. Their account sign-in and payment methods stay private to each person.</p>
        <ul className="space-y-2">
          {household.map((m) => (
            <li key={m.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid={`household-manage-row-${m.id}`}>
              <Avatar name={m.name} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-navy-900">{m.name}</p>
                <p className="truncate text-xs text-slate-500">{m.email || "—"}{m.profileLock ? " · trip departing soon" : ""}</p>
              </div>
              <button onClick={() => setActiveId(m.id)} data-testid={`household-manage-btn-${m.id}`} className={cn(btn.secondary, "h-9 shrink-0")}>Manage Details</button>
            </li>
          ))}
        </ul>
      </div>
      <MemberDialog key={active?.id} member={active} onClose={() => setActiveId(null)} />
    </Card>
  );
}

function ChangePassword() {
  const { changeClientPassword } = useDemo();
  const [v, setV] = useState({ current: "", new: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (v.new.length < 8) return toast.error("New password must be at least 8 characters");
    if (v.new !== v.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    const ok = await changeClientPassword({ current: v.current, new: v.new });
    setBusy(false);
    if (ok) setV({ current: "", new: "", confirm: "" });
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-sm font-semibold text-navy-900">Change password</p>
      <p className="mb-3 mt-1 text-xs text-slate-500">Choose a new password with at least 8 characters.</p>
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
        <label><span className={lbl}>Current</span><input type="password" className={field} value={v.current} onChange={(e) => setV({ ...v, current: e.target.value })} data-testid="client-pw-current" /></label>
        <label><span className={lbl}>New</span><input type="password" className={field} value={v.new} onChange={(e) => setV({ ...v, new: e.target.value })} data-testid="client-pw-new" /></label>
        <label><span className={lbl}>Confirm new</span><input type="password" className={field} value={v.confirm} onChange={(e) => setV({ ...v, confirm: e.target.value })} data-testid="client-pw-confirm" /></label>
        <div className="sm:col-span-3"><button disabled={busy || !v.current || !v.new} className={btn.secondary} data-testid="client-pw-submit"><KeyRound className="h-4 w-4" />Update Password</button></div>
      </form>
    </div>
  );
}

function AccountTab({ me, settings, logout }) {
  const { updateClient } = useDemo();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(me.prefs || {});
  useEffect(() => { setPrefs(me.prefs || {}); }, [me.prefs]);
  const setPref = (k) => (c) => { setPrefs((p) => ({ ...p, [k]: c })); updateClient(me.id, { prefs: { [k]: c } }); };
  return (
    <div className="space-y-4">
      <Card data-testid="client-notifications">
        <CardTitle>Notifications</CardTitle>
        <ul className="divide-y divide-slate-100 px-5 py-1">
          <li><PushMasterToggle enabled={!!me.prefs?.pushEnabled} /></li>
          {[["email", "Email trip updates"], ["sms", "Text message reminders"]].map(([k, l]) => (
            <li key={k} className="flex items-center justify-between py-3.5"><span className="text-sm text-navy-900">{l}</span><Switch data-testid={`client-pref-${k}-switch`} checked={!!prefs[k]} onCheckedChange={setPref(k)} /></li>
          ))}
        </ul>
      </Card>
      <Card data-testid="client-security">
        <CardTitle>Security</CardTitle>
        <div className="p-5 sm:p-6"><ChangePassword /></div>
      </Card>
      <Card data-testid="client-advisor">
        <CardTitle>Your Advisor</CardTitle>
        <div className="flex items-center gap-3 p-5"><Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} /><div className="min-w-0"><p className="text-sm font-semibold text-navy-900">{settings.advisorName}</p><p className="truncate text-xs text-slate-500">{settings.advisorEmail} · {settings.advisorPhone}</p></div></div>
      </Card>
      <button onClick={async () => { await logout(); navigate("/portal"); }} data-testid="client-profile-exit" className={`${btn.secondary} w-full md:w-auto`}><LogOut className="h-4 w-4" />Sign Out</button>
    </div>
  );
}

export default function ClientProfilePage() {
  const { client, settings, logout, companions = [], household = [], householdPrimary = false, me: principal } = useDemo();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "contact");
  const me = client(CURRENT_CLIENT_ID);
  if (!me) return null;
  const lock = principal?.profileLock;

  return (
    <div className="space-y-4" data-testid="client-profile-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl">My Profile</h1>
      <Card className="flex items-center gap-4 p-5">
        <Avatar name={me.name} size="lg" />
        <div className="min-w-0">
          <p className="truncate font-display text-lg font-bold text-navy-900" data-testid="client-profile-name">{me.name}</p>
          <p className="text-sm text-slate-500">Traveling with {settings.agencyName} since {me.since}</p>
        </div>
      </Card>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 gap-1 rounded-xl bg-slate-100 p-1" data-testid="client-profile-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`client-profile-tab-${k}`} className={cn("flex-1 rounded-lg px-1.5 py-1.5 text-center text-xs font-medium transition-colors sm:text-sm", tab === k ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}>{label}</button>
          ))}
        </div>
        {householdPrimary && household.length > 0 && (
          <div className="flex justify-center sm:block">
            <button onClick={() => setTab("household")} data-testid="client-profile-tab-household" className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors sm:gap-2 sm:px-4 sm:py-2 sm:text-sm", tab === "household" ? "bg-navy-600 text-white shadow-sm" : "bg-blue-50 text-navy-700 ring-1 ring-blue-200 hover:bg-blue-100")}>
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />Household Management
            </button>
          </div>
        )}
      </div>

      {tab === "contact" && (
        <div className="max-w-3xl space-y-4">
          {lock && <LockNotice lock={lock} />}
          <EditForm key={`contact-${me.id}`} client={me} testId="client-contact-form" locked={!!lock}
            readonly={[["Email (sign in)", me.email], ["Member since", me.since]]}
            fields={[["phone", "Phone", "tel"], ["dob", "Date of birth", "date"], ["address", "Mailing address", "text", true], ["emergencyName", "Emergency contact name"], ["emergencyPhone", "Emergency contact phone", "tel"]]} />
        </div>
      )}
      {tab === "travel" && (
        <div className="max-w-3xl space-y-4">
          {lock && <LockNotice lock={lock} />}
          {companions.length > 0 && (
            <Card data-testid="client-companions">
              <CardTitle>Travel Companions</CardTitle>
              <ul className="flex flex-wrap gap-2 p-5">
                {companions.map((name) => (
                  <li key={name} className="inline-flex items-center gap-2 rounded-full bg-navy-50 px-3 py-1.5 text-sm font-medium text-navy-800"><Users className="h-3.5 w-3.5 text-navy-500" />{name}</li>
                ))}
              </ul>
            </Card>
          )}
          <EditForm key={`travel-${me.id}`} client={me} testId="client-travel-form" locked={!!lock}
            fields={[["passport", "Passport number"], ["passportCountry", "Issuing country"], ["passportIssued", "Issue date", "date"], ["passportExpires", "Expiration date", "date"], ["knownTraveler", "Known traveler number"], ["accessibility", "Accessibility requirements", "textarea"], ["dietary", "Dietary preferences", "textarea"], ["travelPrefs", "Travel preferences", "textarea"]]} />
        </div>
      )}
      {tab === "loyalty" && <div className="max-w-3xl"><LoyaltyTab client={me} /></div>}
      {tab === "payments" && <CardsPanel clientId={me.id} />}
      {tab === "account" && <div className="max-w-3xl space-y-4"><AccountTab me={me} settings={settings} logout={logout} /></div>}
      {tab === "household" && householdPrimary && household.length > 0 && <div className="max-w-3xl"><HouseholdManager household={household} /></div>}
    </div>
  );
}
